import { useLazyQuery, useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { IonCol, IonGrid, IonRow, useIonPopover } from "@ionic/react";
import { MouseEvent, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import ActionIconButton from "@components/buttons/ActionIconButton";
import BoxIconButton from "@components/buttons/BoxIconButton";
import TertiaryButton from "@components/buttons/TertiaryButton";
import BaseErrors from "@components/fields/BaseErrors";
import Checkbox from "@components/fields/Checkbox";
import MessageDirectUploadFileInput, {
  DirectUploadAndFile,
  Upload
} from "@components/fields/MessageDirectUploadFileInput";
import RichTextArea from "@components/fields/RichTextArea";
import NestedSelect, { Item } from "@components/fieldsNative/NestedSelect";
import Form from "@components/forms/Form";
import styles from "@components/forms/MessageForm.module.css";
import { ResourceData, WorksheetData } from "@components/messaging/Messages";
import Nbsp from "@components/Nbsp";
import BodyCopy from "@components/typography/BodyCopy";
import ExternalLink from "@components/typography/ExternalLink";
import InternalLink from "@components/typography/InternalLink";
import useCreateMessage from "@hooks/mutations/useCreateMessage";
import useFormPersist from "@hooks/useFormPersist";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import { MessageTemplateReplacementEnum } from "@typing/Enums";
import {
  ExpertRoleCreateDocument,
  ExpertRoleCreateMutation,
  ExpertRoleCreateMutationVariables,
  ExpertRoleRoleEnum,
  GetJourneyForMessagesQuery,
  GetMessageTemplatesDocument,
  GetMessageTemplatesQuery,
  GetMessageTemplatesQueryVariables,
  MessageCtaEnum,
  MessageRecordTypeEnum,
  MessageTemplatesSortByEnum,
  MessageTypingDocument,
  MessageTypingMutation,
  MessageTypingMutationVariables
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { IconName } from "@utils/iconUtils";
import { sortByTitle } from "@utils/modelUtils";
import { isIosNativeApp } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";

type FormData = {
  body: string;
  consultCta: boolean;
  directUploadBlobIds?: string[];
};

type Journey = GetJourneyForMessagesQuery["journey"];
type Chat = Journey["chats"][0];

type Props = {
  chat: Chat;
  contentRef?: RefObject<HTMLIonContentElement>;
  includeCta?: boolean;
  journey: Journey;
  messagingDisabled?: boolean;
  onMessageCreate: () => void;
  resourceData: ResourceData | undefined;
  setResourceData: (resourceData: ResourceData | undefined) => void;
  setWorksheetData: (worksheetData: WorksheetData | undefined) => void;
  worksheetData: WorksheetData | undefined;
};

const MessageForm = ({
  chat,
  contentRef,
  includeCta = false,
  journey,
  messagingDisabled = false,
  onMessageCreate,
  resourceData,
  setResourceData,
  setWorksheetData,
  worksheetData
}: Props) => {
  const myExpert = useMyExpert();
  const templateOpener = useOpener(true);
  const [editorRefresh, setEditorRefresh] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [directUploadAndFiles, setDirectUploadAndFiles] = useState<DirectUploadAndFile[]>([]);

  const [getMessageTemplates, getMessageTemplatesQuery] = useLazyQuery<
    GetMessageTemplatesQuery,
    GetMessageTemplatesQueryVariables
  >(GetMessageTemplatesDocument);

  useEffect(() => {
    if (myExpert && templateOpener.isOpen) {
      getMessageTemplates({
        variables: {
          sortBy: MessageTemplatesSortByEnum.title
        }
      });
    }
  }, [getMessageTemplates, myExpert, templateOpener.isOpen]);

  useEffect(() => {
    if (
      myExpert &&
      templateOpener.isOpen &&
      !getMessageTemplatesQuery.loading &&
      getMessageTemplatesQuery.data?.messageTemplates.pageInfo.hasNextPage
    ) {
      getMessageTemplatesQuery.fetchMore({
        variables: { cursor: getMessageTemplatesQuery.data?.messageTemplates.pageInfo.endCursor ?? "" }
      });
    }
  }, [getMessageTemplatesQuery, myExpert, templateOpener.isOpen]);

  const messageTemplates = useNodesFromConnection(getMessageTemplatesQuery.data?.messageTemplates);

  const messageTemplatesGroupedByCategory = useMemo(() => {
    const map = new Map<string, Item[]>();

    sortByTitle(messageTemplates).forEach(item => {
      const key = item.messageTemplateCategory.title;
      const collection = map.get(key);
      const mapItem: { icon: IconName | undefined; label: string; value: string } = {
        icon: journey.usedMessageTemplateIds?.some(id => id === item.id) ? "history" : undefined,
        label: item.title,
        value: item.id
      };
      if (!collection) {
        map.set(key, [mapItem]);
      } else {
        collection.push(mapItem);
      }
    });
    const sortedMap = new Map([...map].sort((a, b) => String(a[0]).localeCompare(b[0])));
    return sortedMap;
  }, [journey.usedMessageTemplateIds, messageTemplates]);

  const busyFiles = useRef<File[]>([]);

  const defaultValues: FormData = {
    body: "",
    consultCta: false,
    directUploadBlobIds: []
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [templateErrors, setTemplateErrors] = useState<string[]>([]);

  const [fileInputBusy, setFileInputBusy] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);

  const [lastTypingMessage, setLastTypingMessage] = useState<Date | undefined>(undefined);
  const typingMessageInterval = 500;

  const createMessage = useCreateMessage({ chat, journeyId: journey.id, onMessageCreate });

  const [messageTyping] = useMutation<MessageTypingMutation, MessageTypingMutationVariables>(MessageTypingDocument);

  const [createExpertRole, { client }] = useMutation<ExpertRoleCreateMutation, ExpertRoleCreateMutationVariables>(
    ExpertRoleCreateDocument,
    {
      update: (_store, { data }) => {
        if (data?.expertRoleCreate?.expertRole) {
          client.resetStore();
        }
      }
    }
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      body: yup.string().required(intl.formatMessage({ id: "errors.message.body.blank" })),
      consultCta: yup.boolean().defined(),
      directUploadBlobIds: yup.array().defined().of(yup.string().defined())
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "all",
    resolver: yupResolver(schema)
  });

  const formPersist = useFormPersist("messageForm", {
    form,
    onDataRestored: () => {
      setEditorRefresh(!editorRefresh);
    },
    uniqueId: `${journey.id}-${chat.id}`
  });

  const bodyWatcher = form.watch("body");

  useEffect(() => {
    if ((bodyWatcher === "<p></p>\n" || bodyWatcher === "") && templateId) {
      setTemplateId(null);
    }
  }, [bodyWatcher, form, templateId]);

  useEffect(() => {
    if (!lastTypingMessage || lastTypingMessage.getTime() < new Date().getTime() - typingMessageInterval) {
      // This isn't amazing, but I can't figure out how else to make it not indicate that the user
      // is typing when the component is initially rendered.
      if (lastTypingMessage) {
        messageTyping({ variables: { chatId: chat.id } });
      }
      setLastTypingMessage(new Date());
    }
  }, [bodyWatcher, chat.id, journey, lastTypingMessage, messageTyping]);

  const scrollToBottom = useCallback(() => {
    contentRef?.current?.scrollToBottom();
  }, [contentRef]);

  const handleRemoveRecord = useCallback(() => {
    setResourceData(undefined);
    setWorksheetData(undefined);
  }, [setResourceData, setWorksheetData]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    setSubmitting(true);
    const messageBody = form.getValues().body;

    if (messageBody.replace("<p></p>", "") === "") {
      form.setError("body", { message: intl.formatMessage({ id: "errors.message.body.blank" }), type: "validate" });
      setSubmitting(false);
      return;
    }

    scrollToBottom();
    return createMessage({
      body: form.getValues().body,
      chatId: chat.id,
      cta: form.getValues().consultCta ? MessageCtaEnum.schedule_consult : null,
      directUploadBlobIds: form.getValues().directUploadBlobIds,
      messageTemplateId: templateId,
      recordId: resourceData?.id ?? worksheetData?.id,
      recordType: resourceData
        ? MessageRecordTypeEnum.RESOURCE
        : worksheetData
          ? MessageRecordTypeEnum.WORKSHEET
          : undefined
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response?.messageCreate,
        intl,
        onSuccess: async () => {
          if (
            response?.messageCreate?.message &&
            myExpert &&
            !journey.expertRoles?.find(role => role.expert.id === myExpert.id)
          ) {
            createExpertRole({
              variables: {
                expertId: myExpert?.id,
                journeyId: journey.id,
                role: ExpertRoleRoleEnum.team_member
              }
            });
          }

          formPersist.clear();
          form.reset();
          setTimeout(scrollToBottom, 350);
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.message"
      });

      handleRemoveRecord();
      setSubmitting(false);
    });
  }, [
    form,
    scrollToBottom,
    createMessage,
    chat.id,
    templateId,
    resourceData,
    worksheetData,
    intl,
    handleRemoveRecord,
    myExpert,
    journey.expertRoles,
    journey.id,
    formPersist,
    createExpertRole
  ]);

  useEffect(() => {
    if (directUploadAndFiles.length > 0) {
      setTimeout(scrollToBottom, 100);
      scrollToBottom();
    }
  }, [directUploadAndFiles.length, scrollToBottom]);

  const errorTemplatePopoverContent = (
    <div className="ion-padding">
      <p>
        <BodyCopy>
          <FormattedMessage id="pages.messages.templateErrors" />
        </BodyCopy>
      </p>
      <p>
        {templateErrors
          .map(error => intl.formatMessage({ id: `pages.expert.sharableUrls.templateKeywords.${error}` }))
          .join(", ")}
      </p>
    </div>
  );

  const [showErrorTemplatePopover] = useIonPopover(<div>{errorTemplatePopoverContent}</div>);

  const valueForTemplateReplacement = (replacement: MessageTemplateReplacementEnum) => {
    switch (replacement) {
      case MessageTemplateReplacementEnum.COMPANY_NAME:
        return journey.owner.employer?.name;
      case MessageTemplateReplacementEnum.EXPERT_FIRST_NAME:
        return journey.primaryExpert?.firstName;
      case MessageTemplateReplacementEnum.OWNER_MEMBER_FIRST_NAME:
        return journey.owner.firstName;
      default:
        return null;
    }
  };

  const handleUseTemplate = (item: Item) => {
    const messageTemplate = messageTemplates.find(template => template.id === item.value);

    if (messageTemplate) {
      let replacedBody = messageTemplate.content;

      const errors: string[] = [];

      Object.values(MessageTemplateReplacementEnum).forEach(replacement => {
        const replacementValue = valueForTemplateReplacement(replacement);

        if (replacementValue) {
          replacedBody = replacedBody.replaceAll(replacement, replacementValue);
        } else if (replacedBody.includes(replacement)) {
          errors.push(replacement);
        }
      });

      form.setValue("body", replacedBody, { shouldValidate: true });
      setEditorRefresh(value => !value);
      setTemplateId(messageTemplate.id);

      if (errors.length > 0) {
        setTemplateErrors(errors);
        showErrorTemplatePopover();
      }
    }
  };

  const handleUploadComplete = useCallback(
    (uploadAndFile: DirectUploadAndFile) => {
      const index = busyFiles.current.indexOf(uploadAndFile.file);
      if (index >= 0) {
        const newBusyFiles = [...busyFiles.current];
        newBusyFiles.splice(index, 1);
        busyFiles.current = newBusyFiles;
        if (newBusyFiles.length === 0) {
          setFileInputBusy(false);
        }
      }
    },
    [busyFiles]
  );

  const handleUploadAndFileDeleted = useCallback(
    (uploadAndFile: DirectUploadAndFile) => {
      handleUploadComplete(uploadAndFile);
      const index = directUploadAndFiles.indexOf(uploadAndFile);
      const newDirectUploadAndFiles = [...directUploadAndFiles];
      newDirectUploadAndFiles.splice(index, 1);
      setDirectUploadAndFiles(newDirectUploadAndFiles);
      form.setValue(
        "directUploadBlobIds",
        newDirectUploadAndFiles.map(item => item.directUpload.signedBlobId)
      );
    },
    [directUploadAndFiles, form, handleUploadComplete]
  );

  const filePreventDefault = useCallback((event: MouseEvent<HTMLIonColElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const saveButtonDisabled = useMemo(
    () =>
      !form?.formState.isValid ||
      form?.formState.isSubmitting ||
      fileInputBusy ||
      submitting ||
      bodyWatcher.replace("<p></p>", "") === "",
    [form?.formState.isSubmitting, form?.formState.isValid, fileInputBusy, submitting, bodyWatcher]
  );

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className={messagingDisabled ? "ion-hide" : undefined}>
        <div className={messagingDisabled ? styles.disabledArea : undefined} />
        <div className={styles.container}>
          <div className={styles.fullWidth}>
            <RichTextArea<FormData>
              contentRefreshSwitch={editorRefresh}
              control={form.control}
              disabled={messagingDisabled}
              error={form.formState.errors.body}
              extraMenuButtons={[
                <MessageDirectUploadFileInput<FormData>
                  busyFiles={busyFiles}
                  control={form.control}
                  directUploadAndFiles={directUploadAndFiles}
                  disabled={messagingDisabled}
                  error={undefined}
                  key="message-file-input"
                  largeDownloadWidth={true}
                  name="directUploadBlobIds"
                  onBusyChange={setFileInputBusy}
                  setDirectUploadAndFiles={setDirectUploadAndFiles}
                  setValue={value => form.setValue("directUploadBlobIds", value)}
                  tabIndex={messagingDisabled ? -1 : 0}
                />
              ]}
              forMessaging
              journeyId={journey.id}
              name="body"
              placeholder={intl.formatMessage({ id: "forms.messageCreate.bodyPlaceholder" })}
              sharableURLSHidden={myExpert?.id ? false : true}
              submitButtonSpacing
            />
          </div>
          <div className={styles.hideMobile}>
            <BoxIconButton
              ariaLabelKey="accessibility.ariaLabels.sendMessage"
              data-test="submit-button"
              disabled={saveButtonDisabled}
              icon="paperPlane"
              size="large"
              type="submit"
            />
          </div>
          <div className={styles.showMobile + " " + styles.mobileSendButton}>
            <BoxIconButton
              ariaLabelKey="accessibility.ariaLabels.sendMessage"
              disabled={saveButtonDisabled}
              icon="paperPlane"
              size="medium"
              type="submit"
            />
          </div>
        </div>
        <div className={styles.textArea}>
          {(resourceData || worksheetData) && (
            <IonGrid className={"ion-no-padding" + " " + styles.data}>
              <IonRow className="ion-align-items-center">
                <IonCol>
                  <BodyCopy color="primary">
                    {worksheetData && (
                      <FormattedMessage
                        id="forms.messageCreate.worksheetInfo"
                        values={{ title: worksheetData.title }}
                      />
                    )}
                    {resourceData && (
                      <FormattedMessage
                        id="forms.messageCreate.resourceInfo"
                        values={{ category: resourceData.taskTitle, title: resourceData.title }}
                      />
                    )}
                  </BodyCopy>
                </IonCol>
                <IonCol size="auto">
                  <ActionIconButton
                    ariaLabelKey="accessibility.ariaLabels.remove"
                    icon="times"
                    onClick={handleRemoveRecord}
                  />
                </IonCol>
              </IonRow>
            </IonGrid>
          )}
          <div className={styles.relativeContainer}>
            {!messagingDisabled && (
              <div>
                {myExpert && (
                  <div className={styles.insertTemplate}>
                    <NestedSelect items={messageTemplatesGroupedByCategory} onSelect={handleUseTemplate}>
                      <TertiaryButton
                        icon="rightToBracket"
                        onClick={templateOpener.open}
                        textKey="pages.messages.insertTemplate"
                      />
                    </NestedSelect>
                  </div>
                )}
                <div className={styles.sendMessage}></div>
              </div>
            )}
            <div className={styles.attachmentsWrapper}>
              <IonGrid className="ion-no-padding">
                <IonRow>
                  {directUploadAndFiles?.map(directUploadAndFile => (
                    <IonCol
                      className={styles.inputGridCol}
                      key={directUploadAndFile.directUpload.blobId}
                      onClick={filePreventDefault}
                      size={"6"}
                    >
                      <Upload
                        onDelete={handleUploadAndFileDeleted}
                        onUploadComplete={handleUploadComplete}
                        uploadAndFile={directUploadAndFile}
                      />
                    </IonCol>
                  ))}
                </IonRow>
              </IonGrid>
            </div>
          </div>
        </div>
      </div>

      {messagingDisabled && (
        <div className="ion-margin-horizontal align-center">
          <div className={styles.hoursUsed}>
            <BodyCopy color="gray">
              <FormattedMessage id="pages.messages.hoursUsed" />
            </BodyCopy>
            <Nbsp />
            {!isIosNativeApp() && (
              <InternalLink
                to={journeyRoute({
                  journeyId: journey.id,
                  linkAfterJourney: "/account?purchaseHours=true",
                  myExpert,
                  preventScroll: false
                })}
              >
                <FormattedMessage id="pages.messages.purchaseHours" />
              </InternalLink>
            )}
          </div>

          <div className={styles.questions}>
            <FormattedMessage id="pages.messages.haveAQuestion" />
            <Nbsp />
            <ExternalLink href="mailto:support@withgrayce.com">
              <FormattedMessage id="pages.messages.contactSupport" />
            </ExternalLink>
          </div>
        </div>
      )}
      {includeCta && (
        <Checkbox<FormData> control={form.control} error={form.formState.errors.consultCta} name="consultCta">
          <FormattedMessage id="forms.messageCreate.ctaLabel" />
        </Checkbox>
      )}
    </Form>
  );
};

export default MessageForm;
